import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { AppConfig } from "../AppConfig";
import { APP_TYPE, AppType } from "../constants/app";
import { PageRoutes } from "../constants/routes";
import {
  ACCOUNT_MANAGEMENT_SIDEBAR_MENUS,
  FILE_UPLOAD_SIDEBAR_MENUS,
} from "../constants/sidebar";
import { LocalStorageManager } from "../core/storage/LocalStorageManager";
import { appActions, appSelectors } from "../ducks/app";
import { authActions, authSelectors } from "../ducks/auth";
import { UserRole } from "../ducks/user";
import { AppDispatch } from "../store";
import { useNavigation } from "./useNavigation";
import { CookieManager } from "../core/storage/CookieManager";

export const useSidebar = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { navigateToPage } = useNavigation();

  const showSidebarSP = useSelector(appSelectors.showSidebarSP);
  const loggedInUser = useSelector(authSelectors.loggedInUser);

  useEffect(() => {
    if (showSidebarSP) dispatch(appActions.hideSidebarSP());
  }, [location.pathname]);

  const username = useMemo(() => {
    if (!loggedInUser) return "";

    return `${loggedInUser.lastName} ${loggedInUser.firstName}`;
  }, [loggedInUser]);

  const clientName = useMemo(() => {
    if (!loggedInUser) return "";

    return `${loggedInUser.customer_name}`;
  }, [loggedInUser]);

  const sidebarMenusToShow = useMemo(() => {
    if (!loggedInUser) return [];

    if (APP_TYPE === AppType.FileUpload) return FILE_UPLOAD_SIDEBAR_MENUS;
    if (APP_TYPE === AppType.AccountManagement) {
      switch (loggedInUser.role) {
        case UserRole.Admin:
          return ACCOUNT_MANAGEMENT_SIDEBAR_MENUS;
        case UserRole.GroupAdmin:
          return ACCOUNT_MANAGEMENT_SIDEBAR_MENUS.filter(
            (menu) => menu.path !== PageRoutes.CLIENTS,
          );
        default:
          break;
      }
    }

    return [];
  }, [loggedInUser]);

  const selectedMenuPath = useMemo(() => {
    return location.pathname;
  }, [location.pathname, sidebarMenusToShow]);

  const onSidebarMenuClick = useCallback((path: string) => {
    navigateToPage(path);
  }, []);

  const onSidebarSPClick = useCallback(() => {
    if (showSidebarSP) dispatch(appActions.hideSidebarSP());
    else dispatch(appActions.showSidebarSP());
  }, [showSidebarSP]);

  const handleLogout = useCallback(() => {
    dispatch(
      appActions.showModal({
        title: "ログアウトしてよろしいですか",
        buttons: [
          {
            text: "キャンセル",
            variant: "dark",
          },
          {
            text: "ログアウト",
            variant: "filled",
            onClick: () => {
              dispatch(appActions.hideModal());
              dispatch(appActions.showLoading());

              dispatch(
                authActions.logout.started({
                  onComplete: () => dispatch(appActions.hideLoading()),
                  onSuccess: () => {
                    LocalStorageManager.Instance.removeObject(
                      AppConfig.Instance.LocalStorageKey["accessToken"],
                    );
                    LocalStorageManager.Instance.removeObject(
                      AppConfig.Instance.LocalStorageKey["loggedInUser"],
                    );
                    CookieManager.Instance.removeObject(
                      AppConfig.Instance.CookieKey["accessToken"],
                      process.env.REACT_APP_COMMON_DOMAIN,
                    );

                    dispatch(
                      appActions.showNotification({
                        type: "success",
                        message: "ログアウトしました",
                      }),
                    );
                  },
                  onError: () => {
                    LocalStorageManager.Instance.removeObject(
                      AppConfig.Instance.LocalStorageKey["accessToken"],
                    );
                    CookieManager.Instance.removeObject(
                      AppConfig.Instance.CookieKey["accessToken"],
                      process.env.REACT_APP_COMMON_DOMAIN,
                    );
                    navigateToPage(PageRoutes.LOGIN);
                  },
                }),
              );
            },
          },
        ],
      }),
    );
  }, []);

  return {
    clientName,
    username,
    showSidebarSP,
    sidebarMenusToShow,
    selectedMenuPath,
    onSidebarMenuClick,
    onSidebarSPClick,
    handleLogout,
  };
};
